// src/components/SystemRecommendation/components/FitsYourFamily.tsx

import React from 'react';
import { FamilyFitData, ViewMode } from '../types';
import CollapsibleSection from './CollapsibleSection';

interface FitsYourFamilyProps {
  data: FamilyFitData;
  viewMode: ViewMode;
}

const FitsYourFamily: React.FC<FitsYourFamilyProps> = ({ data, viewMode }) => {
  return (
    <CollapsibleSection
      title="This is why it fits your family"
      icon="👨‍👩‍👧‍👦"
      confidenceLevel={data.confidence.level}
      summary={data.confidence.summary}
    >
      {viewMode === 'customer' ? (
        <div className="customer-content">
          <div className="fit-section">
            <h4>Your Household</h4>
            <p>
              <strong>{data.occupants.count} people</strong> • {data.occupants.bedrooms} bedrooms • {data.occupants.bathrooms} bathrooms
            </p>
          </div>

          <div className="fit-section">
            <h4>Hot Water Usage</h4>
            <div className="usage-stats">
              <div className="usage-stat">
                <span className="stat-icon">🚿</span>
                <div>
                  <strong>{data.hotWaterDemand.showersPerDay}</strong>
                  <span className="stat-label">showers per day</span>
                </div>
              </div>
              <div className="usage-stat">
                <span className="stat-icon">🛁</span>
                <div>
                  <strong>{data.hotWaterDemand.bathsPerWeek}</strong>
                  <span className="stat-label">baths per week</span>
                </div>
              </div>
            </div>
            <p className="detail-text"><strong>Simultaneous use:</strong> {data.hotWaterDemand.simultaneousUse}</p>
          </div>

          <div className="fit-section">
            <h4>How You Use Hot Water</h4>
            <p className="detail-text"><strong>Peak times:</strong> {data.usage.peak}</p>
            <p className="detail-text"><strong>Typical usage:</strong> {data.usage.typical}</p>
          </div>

          {data.lifestyleConsiderations.length > 0 && (
            <div className="fit-section">
              <h4>Lifestyle Match</h4>
              <ul>
                {data.lifestyleConsiderations.map((item, i) => (
                  <li key={i}>✓ {item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="technical-content">
          <div className="tech-grid">
            <div className="tech-item">
              <span className="tech-label">Occupancy:</span>
              <span className="tech-value">{data.occupants.count} persons</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Bedrooms:</span>
              <span className="tech-value">{data.occupants.bedrooms}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Bathrooms:</span>
              <span className="tech-value">{data.occupants.bathrooms}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Showers/Day:</span>
              <span className="tech-value">{data.hotWaterDemand.showersPerDay}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Baths/Week:</span>
              <span className="tech-value">{data.hotWaterDemand.bathsPerWeek}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Simultaneous Draw:</span>
              <span className="tech-value">{data.hotWaterDemand.simultaneousUse}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Peak Usage:</span>
              <span className="tech-value">{data.usage.peak}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Typical Pattern:</span>
              <span className="tech-value">{data.usage.typical}</span>
            </div>
          </div>
          {data.lifestyleConsiderations.length > 0 && (
            <div className="tech-notes">
              <p><strong>Lifestyle factors:</strong></p>
              <ul>
                {data.lifestyleConsiderations.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
};

export default FitsYourFamily;
